/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/summaryTable.config.json";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_COSTUME_TYPE_INVENTORY_REPORT } from "../graphql";
import { VApplication } from "../../../application";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface ISummaryMethods extends IPluginMethodMap {
  getSummaryProps(args: any): Promise<any>;
}

export class SummaryService implements IAgentPlugin {
  readonly methods: ISummaryMethods;

  constructor() {
    this.methods = {
      getSummaryProps: this.getSummaryProps.bind(this),
    };
  }

  async getSummaryProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    if (!client) {
      return {
        ...config,
        isEmpty: true,
        ultUsers: [],
        totalCount: 0,
        totalPages: 1,
        activePage: 1,
        pageSize: 10,
      };
    }

    const baseFilter: any = {};
    if (args?.criteria?.presentationId) {
      baseFilter.presentationId = args.criteria.presentationId;
    }

    if (
      args?.criteria?.name &&
      Object.prototype.hasOwnProperty.call(args.criteria.name, "contains")
    ) {
      baseFilter.name = {
        contains: args.criteria.name.contains ?? "",
      };
    } else {
      baseFilter.name = {
        contains: "",
      };
    }

    const sectionName =
      typeof args?.criteria?.sectionName === "string"
        ? args.criteria.sectionName
        : args?.criteria?.sectionName?.eq;

    if (sectionName) {
      baseFilter.sectionName = {
        eq: sectionName,
      };
    }

    if (!baseFilter.presentationId) {
      return {
        ...config,
        ultUsers: [],
        isEmpty: true,
        totalCount: 0,
        totalPages: 1,
        activePage: 1,
        pageSize: 10,
      };
    }

    const options = {
      limit: args?.size ?? 2000,
      skip: args?.offset ?? 0,
    };

    try {
      const [limitRes, registeredRes, remainingRes] = await Promise.all([
        client.query({
          query: GET_COSTUME_TYPE_INVENTORY_REPORT,
          variables: {
            filter: {
              ...baseFilter,
              inventoryType: "inventLimit",
            },
            options,
          },
          fetchPolicy: "network-only",
        }),

        client.query({
          query: GET_COSTUME_TYPE_INVENTORY_REPORT,
          variables: {
            filter: {
              ...baseFilter,
              inventoryType: "registered",
            },
            options,
          },
          fetchPolicy: "network-only",
        }),

        client.query({
          query: GET_COSTUME_TYPE_INVENTORY_REPORT,
          variables: {
            filter: {
              ...baseFilter,
              inventoryType: "remaining",
            },
            options,
          },
          fetchPolicy: "network-only",
        }),
      ]);

      const limitReport = limitRes?.data?.getCostumeTypeInventoryReport;

      const registeredReport =
        registeredRes?.data?.getCostumeTypeInventoryReport;

      const remainingReport = remainingRes?.data?.getCostumeTypeInventoryReport;

      const limitContent = limitReport?.content || [];

      const registeredContent = registeredReport?.content || [];

      const remainingContent = remainingReport?.content || [];

      const rowsMap: Record<string, any> = {};

      const getRowKey = (item: any) => {
        return [
          item?.id || item?.costumeType || "",
          item?.sectionName || "",
        ].join("-");
      };

      const createEmptyRow = (item: any) => {
        const key = getRowKey(item);

        return {
          id: item?.id || key,
          label: item?.costumeType || "-",
          sectionName: item?.sectionName || "-",

          inventoryLimit: {
            default: 0,
            specialReserve: 0,
            sectionSpecific: 0,
            total: 0,
          },

          registered: {
            default: 0,
            specialReserve: 0,
            sectionSpecific: 0,
            total: 0,
          },

          remaining: {
            default: 0,
            specialReserve: 0,
            sectionSpecific: 0,
            total: 0,
          },
        };
      };

      const getInventoryValues = (item: any) => ({
        default: item?.default ?? 0,
        specialReserve: item?.specialReserve ?? 0,
        sectionSpecific: item?.sectionSpecific ?? 0,
        total: item?.total ?? 0,
      });

      limitContent.forEach((item: any) => {
        const key = getRowKey(item);

        if (!rowsMap[key]) {
          rowsMap[key] = createEmptyRow(item);
        }

        rowsMap[key].inventoryLimit = getInventoryValues(item);
      });

      registeredContent.forEach((item: any) => {
        const key = getRowKey(item);

        if (!rowsMap[key]) {
          rowsMap[key] = createEmptyRow(item);
        }

        rowsMap[key].registered = getInventoryValues(item);
      });

      remainingContent.forEach((item: any) => {
        const key = getRowKey(item);

        if (!rowsMap[key]) {
          rowsMap[key] = createEmptyRow(item);
        }

        rowsMap[key].remaining = getInventoryValues(item);
      });

      const ultUsers = Object.values(rowsMap);

      const count = limitReport?.count ?? ultUsers.length;

      const skip = limitReport?.skip ?? options.skip;

      const limit = limitReport?.limit ?? options.limit;

      const totalCount = count;
      const pageSize = limit;

      const totalPages = Math.ceil(totalCount / pageSize) || 1;

      const activePage = Math.floor(skip / pageSize) + 1;

      return {
        ...config,
        ultUsers,
        isEmpty: ultUsers.length <= 0,
        totalCount,
        totalPages,
        activePage,
        pageSize,
      };
    } catch (err: any) {
      console.log("error in getSummaryProps", err);

      return {
        ...config,
        isEmpty: true,
        ultUsers: [],
        totalCount: 0,
        totalPages: 1,
        activePage: 1,
        pageSize: 10,
      };
    }
  }
}
